import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter, useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Divider, InputAdornment } from '@material-ui/core';
import Formsy from 'formsy-react';
import { showMessage, addSubProducer, editUser } from 'app/store/actions';
import PermissionSelector from '../../custom-widgets/PermissionsSelector/PermissionSelector';
import { SelectSearchFormsy } from '../../../@fuse/components/formsy';
import { TextFieldFormsy } from '@fuse';
import { formatUserErrorMessage, getUserRegion } from '../../../utils';
import { Alert } from '@mui/material';
import { regionCountryList, regionStateList, state } from '../form-components/ReactHookProducerFormSelects';
import { applyLinkSelection, ExistingIdentityLinkSection, LinkGuardedButton, LINK_MODES, runLinkCreate, useIdentityLinkState } from './components/ExistingIdentityLinkControl';
import ManageNotificationsLink from '../form-components/ManageNotificationsLink';
import _ from 'lodash';

const SubProducerForm = ({ editData, producers, preselectedPermissions, disabled, permissionType }) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const region = getUserRegion();
    const history = useHistory();
    const [canSubmit, setCanSubmit] = useState(false);
    const [alert, setAlert] = useState(null);
    const link = useIdentityLinkState();
    const [country, setCountry] = useState(editData ? editData.country : '');
    const basePermissions = editData?.permissions || preselectedPermissions || [];
    const isStockHolder = editData?.permissions == null ? false : editData?.permissions.includes('ManageEquity');

    const [permissions, setPermissions] = useState(new Set(basePermissions));

    const showError = !permissions.size;
    const lastInitIdRef = useRef(null);

    // Sync permissions when editData first arrives for a given user (useState
    // initializer only runs once). Track user ID so navigating to a different
    // subuser re-initializes, while react-query refetches of the same user don't
    // overwrite in-progress edits.
    useEffect(() => {
        const userId = editData?._id;
        if (editData?.permissions && userId !== lastInitIdRef.current) {
            lastInitIdRef.current = userId;
            setPermissions(new Set([...editData.permissions, ...(preselectedPermissions || [])]));
        }
    }, [editData, preselectedPermissions]);

    const disableButton = () => {
        setCanSubmit(false);
    };

    const enableButton = () => {
        setCanSubmit(true);
    };

    const handlePermissionChange = (newPermissions) => {
        setPermissions(newPermissions);
    };

    const navigateToManageUsers = () => {
        history.replace({ pathname: '/manage/sub-users' });
    };

    const onCountryChange = (event) => {
        setCountry(event.target.value);
    };

    const invalidateSubUserTable = () => {
        queryClient.invalidateQueries({ queryKey: 'sub-user-table' });
    };

    const onSubmit = (model) => {
        const submitModel = { ...model };

        submitModel.permissions = Array.from(permissions);

        if (editData) {
            delete submitModel.username;
            delete submitModel.email;
            dispatch(editUser(submitModel, editData._id))
                .then(() => {
                    dispatch(showMessage({ message: 'Successfully Edited Sub Producer.' }));
                    invalidateSubUserTable();
                    setAlert(null);
                    navigateToManageUsers();
                })
                .catch((err) => {
                    dispatch(showMessage({ message: formatUserErrorMessage(err, 'Sub Producer', 'edit') }));
                });
        } else {
            if (link.submitGuard()) {
                // PFM-3029: confirm the identity in the link control before submitting.
                return;
            }
            runCreate(applyLinkSelection(submitModel, link.linkSelection));
        }
    };

    const runCreate = (createModel) => {
        runLinkCreate({
            action: (model) => dispatch(addSubProducer(model)),
            model: { ...createModel, type_of_fluid: producers ? producers[0].type_of_fluid : 'milk' },
            onSuccess: () => {
                dispatch(showMessage({ message: 'Successfully Added Sub Producer.' }));
                invalidateSubUserTable();
                setAlert(null);
                navigateToManageUsers();
            },
            onError: (err) => {
                dispatch(showMessage({ message: formatUserErrorMessage(err, 'Sub Producer', 'add') }));
                if (err.message) {
                    setAlert(err.message);
                }
            },
            onLinkOffer: (prompt) => link.setLinkPrompt(prompt),
            onResetLink: (msg) => dispatch(showMessage({ message: link.resetLink(msg) })),
        });
    };
    return (
        <div className="min-w-3/4 max-w-3/4">
            <Divider />

            <Formsy
                onValidSubmit={onSubmit}
                onValid={enableButton}
                onInvalid={disableButton}
                /* eslint-disable-next-line no-return-assign */
                ref={(form) => form}
                className="flex flex-col justify-center"
            >
                <ExistingIdentityLinkSection targetRole="producer" isSubuser show={!editData} link={link} onConfirmLink={() => link.confirmLinkPrompt(runCreate)} />

                {alert && (
                    <Alert className="mt-32 mb-16" color="error">
                        {alert}
                    </Alert>
                )}

                <TextFieldFormsy
                    className="my-16"
                    type="text"
                    name="name"
                    label="Name"
                    value={editData ? editData.name : ''}
                    validations={{ minLength: 2 }}
                    validationErrors={{ minLength: 'Min character length is 2' }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <i className="text-20 material-icons" color="action">
                                    person
                                </i>
                            </InputAdornment>
                        ),
                    }}
                    required
                    variant="outlined"
                />

                {link.linkMode !== LINK_MODES.LINK && (
                    <TextFieldFormsy
                        className="my-16"
                        type="text"
                        name="username"
                        label="Username"
                        value={editData ? editData.username : ''}
                        validations={{ ...(!editData && { minLength: 4 }) }}
                        validationErrors={{ minLength: 'Min character length is 4' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <i className="text-20 material-icons" color="action">
                                        bookmark
                                    </i>
                                </InputAdornment>
                            ),
                        }}
                        required={!(link.linkMode === LINK_MODES.LINK)}
                        variant="outlined"
                        disabled={!!editData || link.linkMode === LINK_MODES.LINK}
                    />
                )}

                {!editData && link.linkMode === LINK_MODES.NEW && (
                    <TextFieldFormsy
                        className="my-16"
                        type="password"
                        name="password"
                        label="Password"
                        validations={{ minLength: 8, equalsField: 'password_confirmation' }}
                        validationErrors={{ equalsField: 'Passwords do not match', minLength: 'Password must be at least 8 characters' }}
                        InputProps={{
                            autoComplete: 'new-password',
                            endAdornment: (
                                <InputAdornment position="end">
                                    <i className="text-20 material-icons" color="action">
                                        vpn_key
                                    </i>
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                        required
                    />
                )}

                {!editData && link.linkMode === LINK_MODES.NEW && (
                    <TextFieldFormsy
                        className="my-16"
                        type="password"
                        name="password_confirmation"
                        label="Confirm Password"
                        validations={{ minLength: 8, equalsField: 'password' }}
                        validationErrors={{ equalsField: 'Passwords do not match', minLength: 'Password must be at least 8 characters' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <i className="text-20 material-icons" color="action">
                                        vpn_key
                                    </i>
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                        required
                    />
                )}

                {link.linkMode !== LINK_MODES.LINK && (
                    <TextFieldFormsy
                        className="my-16"
                        type="text"
                        name="email"
                        label="Email"
                        value={editData ? editData.email : ''}
                        validations="isEmail"
                        validationError="This is not a valid email"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <i className="text-20 material-icons" color="action">
                                        alternate_email
                                    </i>
                                </InputAdornment>
                            ),
                        }}
                        required={!(link.linkMode === LINK_MODES.LINK)}
                        variant="outlined"
                        disabled={!!editData || link.linkMode === LINK_MODES.LINK}
                    />
                )}

                <TextFieldFormsy
                    className="my-16"
                    type="text"
                    name="secondary_email"
                    label="Secondary Email"
                    validations="isEmail"
                    value={editData ? editData.secondary_email : ''}
                    validationError="This is not a valid email"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <i className="text-20 material-icons" color="action">
                                    alternate_email
                                </i>
                            </InputAdornment>
                        ),
                    }}
                    variant="outlined"
                />

                <SelectSearchFormsy
                    className="my-16"
                    name="producer_id"
                    label="Producer"
                    variant="standard"
                    options={[{ value: ' ', name: 'None' }, ...producers.map((entry) => ({ value: entry.value, name: entry.name }))]}
                    value={editData ? editData.producer_id : producers.length === 1 ? producers[0].value : ' '}
                    validations="minLength:2"
                    validationError="Please select a Producer"
                    disabled={!!editData || producers.length === 1}
                />
                {(permissionType === 'Stock-Holder' || isStockHolder) && ['PRAIRIE'].includes(region) && (
                    <>
                        <TextFieldFormsy
                            className="my-16"
                            type="text"
                            name="equity_id"
                            placeholder="Equity ID"
                            label="Equity ID"
                            value={editData ? editData.equity_id : ''}
                            validationErrors={{ minLength: 'Min character length is 2' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <i className="text-20 material-icons" color="action">
                                            list_alt
                                        </i>
                                    </InputAdornment>
                                ),
                            }}
                            required
                            variant="outlined"
                        />
                        <TextFieldFormsy
                            className="my-16"
                            type="text"
                            name="mailing_address1"
                            label={'Mailing Address Line 1'}
                            placeholder={'Mailing Address Line 1'}
                            value={editData ? editData.mailing_address1 : ''}
                            validations={{ minLength: 2 }}
                            validationError="Please enter a valid address"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <i className="text-21 material-icons" color="action">
                                            home
                                        </i>
                                    </InputAdornment>
                                ),
                            }}
                            required
                            variant="outlined"
                        />
                        <TextFieldFormsy
                            className="my-16"
                            type="text"
                            name="mailing_address2"
                            label="Mailing Address Line 2"
                            placeholder="Mailing Address Line 2"
                            value={editData ? editData.mailing_address2 : ''}
                            validations={{ minLength: 2 }}
                            validationError="Please enter a valid address"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <i className="text-21 material-icons" color="action">
                                            home
                                        </i>
                                    </InputAdornment>
                                ),
                            }}
                            variant="outlined"
                        />
                        <TextFieldFormsy
                            className="my-16"
                            type="text"
                            name="mailing_city"
                            label={'Mailing City'}
                            placeholder={'Mailing City'}
                            value={editData ? editData.mailing_city : ''}
                            validations={{ minLength: 2 }}
                            validationError="Please enter a valid mailing city"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <i className="text-21 material-icons" color="action">
                                            place
                                        </i>
                                    </InputAdornment>
                                ),
                            }}
                            required
                            variant="outlined"
                        />
                        {country === 'US' && (
                            <SelectSearchFormsy
                                className="my-16"
                                name="mailing_state"
                                label="Mailing State"
                                placeholder={'Mailing State'}
                                value={editData ? editData.mailing_state : ''}
                                options={_.isEmpty(regionStateList(region)) ? state : regionStateList(region)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <i className="text-21 material-icons" color="action">
                                                place
                                            </i>
                                        </InputAdornment>
                                    ),
                                }}
                                required
                                variant="outlined"
                            />
                        )}
                        {country !== 'US' && (
                            <TextFieldFormsy
                                className="my-16"
                                type="text"
                                name="mailing_state"
                                label="Mailing State"
                                placeholder="Mailing State"
                                value={editData ? editData.mailing_state : ''}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <i className="text-21 material-icons" color="action">
                                                home
                                            </i>
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                            />
                        )}
                        <SelectSearchFormsy
                            className="my-16"
                            name="mailing_country"
                            label="Mailing Country"
                            placeholder={'Mailing Country'}
                            displayEmpty
                            value={editData ? editData.mailing_country : ''}
                            options={regionCountryList}
                            onChange={onCountryChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <i className="text-21 material-icons" color="action">
                                            place
                                        </i>
                                    </InputAdornment>
                                ),
                            }}
                            required
                            variant="outlined"
                        />
                        <TextFieldFormsy
                            className="my-16"
                            type="text"
                            name="mailing_zip"
                            label={'Mailing Postal / ZIP Code'}
                            placeholder={'Mailing Postal / ZIP Code'}
                            value={editData ? editData.mailing_zip : ''}
                            validations="minLength: 4"
                            validationError="Please enter a valid postal code"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <i className="text-21 material-icons" color="action">
                                            mail_outline
                                        </i>
                                    </InputAdornment>
                                ),
                            }}
                            required
                            variant="outlined"
                        />
                    </>
                )}

                <PermissionSelector
                    permissionType={permissionType}
                    handlePermissionChange={handlePermissionChange}
                    /* eslint no-unneeded-ternary:0 */
                    selectedValues={permissions}
                    disabled={disabled}
                    showError={showError}
                />

                <ManageNotificationsLink userId={editData?._id} />

                <LinkGuardedButton blocked={link.submitGuard()} type="submit" variant="contained" color="primary" className="mx-auto my-16" aria-label={editData ? 'EDIT SUB USER' : 'ADD SUB USER'} disabled={disabled || !canSubmit}>
                    {editData ? 'Edit Sub User' : 'Add Sub User'}
                </LinkGuardedButton>
            </Formsy>
        </div>
    );
};

export default withRouter(SubProducerForm);
